import { jsPDF } from "jspdf"
import html2canvas from "html2canvas"

/**
 * Creates a promise that resolves after the specified number of milliseconds.
 * Used to introduce delays in asynchronous operations.
 *
 * @param {number} ms - The number of milliseconds to wait
 * @returns {Promise<void>} A promise that resolves after the specified delay
 */
const waiting = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Generates a PDF document from the current page content using html2canvas and jsPDF.
 * The function captures a screenshot of the root element and converts it to a PDF with
 * proper page breaks and formatting.
 *
 * @param {string} last_name - The last name to be included in the PDF filename
 * @param {string} title - The title of the document (affects page orientation and units)
 * @param {boolean} [isdbt] - Optional flag to determine which elements to ignore during capture
 * @returns {Promise<void>} A promise that resolves when the PDF is generated and saved
 *
 * @example
 * await generatePdf("Smith", "Assessment", false)
 * // Generates: Vira_Assessment_Smith.pdf
 */
export const generatePdf = async (
  last_name: string,
  title: string,
  isdbt?: boolean
) => {
  await waiting(3000)

  const pdf = new jsPDF("p", title === "Assessment" ? "mm" : "pt", "A4", true) // using "pt" for assessment tab causes the text to distorted at the page break
  const pageHeight = pdf.internal.pageSize.getHeight()
  let position = 0

  const ele = document.getElementById("root")

  html2canvas(ele!, {
    scale: 1,
    windowWidth: ele?.scrollWidth,
    windowHeight: ele?.scrollHeight,
    ignoreElements: (element) =>
      [
        "spinner",
        "dateRange",
        isdbt ? "toggle" : "assessment-accordion",
      ].includes(element.id),
  }).then((canvas) => {
    const imgData = canvas.toDataURL("image/png", 1.0)
    const imgHeight =
      (canvas.height * pdf.internal.pageSize.getWidth()) / canvas.width - 10
    while (position < imgHeight) {
      pdf.addImage(
        imgData,
        "PNG",
        0,
        -position,
        pdf.internal.pageSize.getWidth(),
        imgHeight,
        "FAST"
      )
      position += pageHeight

      if (position < imgHeight) {
        pdf.addPage()
      }
    }

    pdf.save(`Vira_${title}_${last_name}.pdf`)
  })
}

/**
 * Generates a PDF document for a safety plan based on selected options and client information.
 * The function processes different types of safety plan sections and formats them into a
 * structured PDF document with proper styling.
 *
 * @param {Map<string, any>} selectedOptions - A map containing safety plan sections and their selected options
 * @param {string} client_name - The name of the client for whom the safety plan is being generated
 * @returns {Promise<void>} A promise that resolves when the safety plan PDF is generated and saved
 *
 * @description
 * The function handles various safety plan option types:
 * - select_all: Standard text responses
 * - place: Location-based entries with place names
 * - text_box: Custom text entries
 * - contact: Contact information with first name, last name, and phone
 * - radio: Single selection options
 *
 * @example
 * const options = new Map([["section1", { header: "Warning Signs", selectedOptionsMap: new Map() }]])
 * await generateSafetyPlanPdf(options, "John Doe")
 * // Generates: Safety_Plan_John Doe.pdf
 */
export const generateSafetyPlanPdf = async (
  selectedOptions: Map<string, any>,
  client_name: string
) => {
  const pdf = new jsPDF("p", "px", "a4")
  const sectionsArr = [...selectedOptions.values()]
  let str =
    "<div style='padding: 20px;font-size: 20px; font-family: Lato;line-height: 16px;font-style: normal;font-weight: 200;'>"

  sectionsArr.forEach(function (section) {
    const optionsArr = [...section.selectedOptionsMap.values()]
    str += `<h3 style="font-size: 15px;">${section.header}</h3>`
    str += "<ul style='margin: 0; padding: 0;'>"
    if (optionsArr.length > 0) {
      optionsArr.forEach(function (option) {
        if (option.category_type === "select_all")
          str += `<li style="padding-left: 3px;font-size:8px;">${option.response_text}</li>`
        else if (option.category_type === "place") {
          for (let custom of option.custom_values) {
            str += `<li style="padding-left: 3px;font-size: 8px;">${custom.place}</li>`
          }
        } else if (option.category_type === "text_box") {
          for (let custom of option.custom_values) {
            str += `<li style="padding-left: 3px;font-size: 8px">${custom}</li>`
          }
        } else if (option.category_type === "contact") {
          for (let custom of option.custom_values) {
            str += `<li style="padding-left: 3px;font-size: 8px">${custom.first} ${custom.last}, ${custom.phone}</li>`
          }
        } else if (option.category_type === "radio") {
          str += `<li style="padding-left: 3px;font-size: 8px">${option.custom_values[0]}</li>`
        }
      })
    }
    str += "</ul>"
  })

  str += "</div>"

  const html = `
  <html>
    <head>
      <title>${client_name}'s Safety Plan</title>
    </head>
    <body>
      <div style="width:1600px">
        <div style="padding: 10px;font-size: 20px; font-family: Lato;line-height: 16px;font-style: "normal";font-weight: 200; border: 1px solid green;>${client_name}'s Safety Plan</div>
        ${str}
      </div>
    </body>
  </html>
  `

  pdf.html(html, {
    callback: function (pdf) {
      pdf.save(`Safety_Plan_${client_name}.pdf`)
    },
    autoPaging: "text",
  })
}
